// S7 Kalender — monthly grid (own implementation, no library).
//
// Grid: 7-col CSS-grid (Mo–So, ISO week-start). The visible month is padded
// with leading days from the previous month and trailing days from the next
// month so the grid always shows complete weeks (35 or 42 cells).
//
// Task placement:
// - "single-day": deadline OR (start_date == end_date) → rendered as a chip
//   inside that day's cell.
// - "multi-day": start_date < end_date → rendered as a horizontal bar that
//   spans the day-cells it touches. To keep layout simple, each spanning bar
//   sits in its own row segment per week (we break the bar at week
//   boundaries and re-draw on Mondays) — the pure split lives in
//   lib/calendarBars.splitTaskIntoWeekSegments so it's unit-testable
//   without mounting this view. A segment whose edge is a week-boundary
//   clip (not the task's real start/end) is rendered with that corner
//   flattened instead of rounded, so consecutive weekly segments read as
//   one continuing bar rather than several unrelated ones (#42). Each
//   weekly segment is one absolutely positioned bar over its row of
//   day-cells — perfect Gantt-style overlap layout is explicitly out of
//   scope per Phase 1.5 spec.
//
// Click on a day cell opens a side-panel listing all tasks for that day,
// sorted by priority (urgent → high → medium → low → none). Clicking a task
// (chip, bar, or panel-entry) opens the TaskModal.
//
// #876 drag & drop — chosen semantics (see computeChipDatePatch /
// computeSpanShiftPatch below for the pure date math):
// - Single-day chip: represents whichever field placed it on that day
//   (`deadline ?? end_date ?? start_date`, same priority classifyTasks()
//   uses to choose the cell). Dropping it onto another day rewrites exactly
//   that field. The one exception is a same-day start_date===end_date task
//   with no deadline — both fields are rewritten together so the task stays
//   a zero-duration span on its new day instead of becoming inconsistent.
// - Multi-day bar: each weekly segment is its own Draggable (unchanged
//   visual — one bar per week it crosses), tagged with the ISO date of the
//   segment's own first visible day as its drag "anchor". Dropping it onto a
//   day cell computes the day-delta between that anchor and the target day
//   and shifts BOTH start_date and end_date by the same delta — the span's
//   duration is always preserved, never collapsed to a single day. A bar
//   dropped back onto its own anchor day, or onto the (day-less) bars row
//   itself rather than a specific day cell, is a no-op.
// - Undated tasks never render a chip or bar here (classifyTasks skips them
//   entirely), so there's nothing to drag — no semantics needed for that
//   case in this view.
// - Read-only viewers get no drag affordance at all (isDragDisabled).

import { useEffect, useMemo, useState } from "preact/hooks";
import { ChevronLeft, ChevronRight, Diamond, X } from "lucide-preact";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import type {
  Phase,
  ProjectWithCounts,
  Task,
  TaskPriority,
} from "@juuplan/shared";
import {
  addDaysIso,
  diffDaysIso,
  TASK_PRIORITY_LABEL,
  TASK_PRIORITY_VAR,
  TASK_STATUS_LABEL,
  TASK_STATUS_VAR,
  softPillStyle,
} from "../lib/format";
import { pickFg } from "../components/LabelChip";

/**
 * Vordergrundfarbe fuer einen farbigen Kalender-Balken.
 *
 * `pickFg()` rechnet die Luminanz eines HEX-Werts aus. Eine CSS-Variable kann
 * es NICHT lesen und faellt dann still auf Weiss zurueck — und genau das
 * passierte hier: `projectColor` ist `project.color ?? "var(--color-primary)"`
 * (Zeile ~221), ein Projekt ohne eigene Farbe lieferte also die Zeichenkette
 * `"var(--color-primary)"` an `pickFg()`. Im hellen Theme war Weiss zufaellig
 * richtig; im DUNKELMODUS ist `--color-primary` ein helles Blau (`#6293ff`),
 * weisser Text darauf ergibt rund 2,3:1 und liegt damit unter der AA-Grenze.
 *
 * Fuer Token-Farben nehmen wir deshalb direkt `--color-on-accent` (das Theme
 * dreht den Wert im Dunkelmodus auf Fast-Schwarz). Nutzergewaehlte Farben sind
 * weiter HEX und gehen unveraendert durch `pickFg()`.
 *
 * Dasselbe Muster steht als `barFg()` in `views/GanttView.tsx`.
 */
function barFg(color: string): string {
  return color.startsWith("var(") ? "var(--color-on-accent)" : pickFg(color);
}
import { useMe } from "../lib/auth";
import { findUser } from "../store/users";
import { TaskModal } from "../components/TaskModal";
import { relativeDay } from "../components/TaskCard";
import { BotBadge } from "../components/BotBadge";
import { patchTask, refreshProjectDetail } from "../store/project";
import { toastError } from "../lib/toast";
import { matchesAllFilters, type FilterRule } from "../lib/viewState";
import { projectTaskToRecord } from "../lib/taskRecord";
import { splitTaskIntoWeekSegments } from "../lib/calendarBars";

// Shared dnd item-type so a chip/bar Draggable can be dropped onto ANY day
// cell across the whole grid, and also so the (day-less) "bars row"
// Droppable can hold bar Draggables as a source without ever being a valid
// drop destination (see computeSpanShiftPatch's anchor-day requirement).
const CAL_DND_TYPE = "CAL_ITEM";

interface Props {
  project: ProjectWithCounts;
  tasksByPhase: Record<number, Task[]>;
  /** Viewer-Rolle: TaskModal read-only. */
  readOnly?: boolean;
  /**
   * Shared view filters from Project.tsx. This view previously did NO filtering
   * at all, so the type/hide-done controls had no effect here (#872/#873). It
   * now runs the same `matchesAllFilters` predicate as every other view instead
   * of growing a fourth hand-written one.
   */
  filters?: FilterRule[];
}

const WEEKDAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"] as const;
const MONTH_NAMES_DE = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];
const PRIO_RANK: Record<TaskPriority | "none", number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

export function CalendarView(props: Props): preact.JSX.Element {
  const { project, tasksByPhase, readOnly = false, filters } = props;
  const me = useMe();

  const allTasks = useMemo(() => {
    const out: Task[] = [];
    for (const list of Object.values(tasksByPhase)) out.push(...list);
    if (!filters || filters.length === 0) return out;
    // Project tasks are `Task`; the shared predicate speaks `TaskRecord`. The
    // existing projection bridges the two, so filter semantics stay in one
    // place instead of being reimplemented per view.
    return out.filter((t) => matchesAllFilters(projectTaskToRecord(t), filters));
  }, [tasksByPhase, filters]);

  const phaseById = useMemo(() => {
    const m: Record<number, Phase> = {};
    for (const p of project.phases) m[p.id] = p;
    return m;
  }, [project.phases]);

  const taskById = useMemo(() => {
    const m = new Map<number, Task>();
    for (const t of allTasks) m.set(t.id, t);
    return m;
  }, [allTasks]);

  // Current visible month (1st of month).
  const [cursor, setCursor] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [onlyMine, setOnlyMine] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string | null>(null); // ISO YYYY-MM-DD
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);

  // Deep-link open: ?task=<id> in the URL opens the matching task modal
  // immediately (mirrors TableView's handling for the same query param).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("task");
    if (!raw) return;
    const id = Number.parseInt(raw, 10);
    if (Number.isNaN(id)) return;
    setOpenTaskId(id);
    params.delete("task");
    const query = params.toString();
    const next =
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState({}, "", next);
  }, [project.id]);

  const visibleTasks = useMemo(
    () =>
      onlyMine && me.user
        ? allTasks.filter((t) => me.user && t.assignee_ids.includes(me.user.id))
        : allTasks,
    [allTasks, onlyMine, me.user]
  );

  // Build the 35/42-day grid for the month.
  const grid = useMemo(() => buildGrid(cursor), [cursor]);
  const weeks = useMemo(() => chunkWeeks(grid), [grid]);

  // Classify each task into single-day-by-date or multi-day-range.
  // singleDayMap: ISO-YYYY-MM-DD → tasks
  // multiDayBars: each task with start < end becomes one bar per week-row it touches
  const { singleDayMap, multiDayPerWeek } = useMemo(
    () => classifyTasks(visibleTasks, weeks),
    [visibleTasks, weeks]
  );

  const today = isoLocal(new Date());

  const monthLabel = `${MONTH_NAMES_DE[cursor.getMonth()]} ${cursor.getFullYear()}`;

  const goPrev = (): void => {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
    setSelectedDay(null);
  };
  const goNext = (): void => {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
    setSelectedDay(null);
  };
  const goToday = (): void => {
    const now = new Date();
    setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDay(null);
  };

  const openTask = visibleTasks.find((t) => t.id === openTaskId) ?? null;

  const tasksForSelectedDay = selectedDay
    ? collectDayTasks(visibleTasks, selectedDay).sort(
        (a, b) =>
          PRIO_RANK[a.priority ?? "none"] - PRIO_RANK[b.priority ?? "none"] ||
          a.title.localeCompare(b.title, "de")
      )
    : [];

  const projectColor = project.color ?? "var(--color-primary)";

  // See the DnD-semantics comment block at the top of this file. Chips use
  // draggableId "chip:<taskId>"; bar segments use
  // "bar:<taskId>:<segmentAnchorIso>". Every day cell is a Droppable with id
  // "day:<iso>" — dropping onto anything else (the day-less bars row, or no
  // destination at all) is a no-op because we can't resolve a target date.
  const onDragEnd = async (result: DropResult): Promise<void> => {
    if (readOnly) return;
    const { destination, draggableId } = result;
    if (!destination) return;
    if (!destination.droppableId.startsWith("day:")) return;
    const targetIso = destination.droppableId.slice("day:".length);

    if (draggableId.startsWith("chip:")) {
      const taskId = Number(draggableId.slice("chip:".length));
      const task = taskById.get(taskId);
      if (!task) return;
      const patch = computeChipDatePatch(task, targetIso);
      if (!patch) return;
      try {
        await patchTask(taskId, patch);
      } catch {
        toastError("Datum konnte nicht geändert werden – bitte erneut versuchen");
        void refreshProjectDetail();
      }
      return;
    }

    if (draggableId.startsWith("bar:")) {
      const rest = draggableId.slice("bar:".length);
      const sep = rest.indexOf(":");
      if (sep < 0) return;
      const taskId = Number(rest.slice(0, sep));
      const anchorIso = rest.slice(sep + 1);
      const task = taskById.get(taskId);
      if (!task || !task.start_date || !task.end_date) return;
      const patch = computeSpanShiftPatch(task.start_date, task.end_date, anchorIso, targetIso);
      if (!patch) return;
      try {
        await patchTask(taskId, patch);
      } catch {
        toastError("Zeitraum konnte nicht verschoben werden – bitte erneut versuchen");
        void refreshProjectDetail();
      }
    }
  };

  return (
    <div class="flex flex-col gap-3">
      {/* Toolbar */}
      <div class="flex flex-wrap items-center gap-2">
        <div class="inline-flex items-center gap-1 ml-auto" role="group" aria-label="Monat wechseln">
          <button
            type="button"
            onClick={goPrev}
            class="inline-flex items-center justify-center rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-1.5 text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
            aria-label="Vorheriger Monat"
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>
          <span
            class="px-3 py-1 text-sm font-semibold text-[color:var(--color-on-surface)] min-w-[140px] text-center"
            aria-live="polite"
          >
            {monthLabel}
          </span>
          <button
            type="button"
            onClick={goNext}
            class="inline-flex items-center justify-center rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-1.5 text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
            aria-label="Nächster Monat"
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={goToday}
            class="ml-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          >
            Heute
          </button>
        </div>

        {/* Mine / All toggle */}
        <div class="inline-flex items-center rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-0.5" role="group" aria-label="Filter Aufgaben">
          <button
            type="button"
            onClick={() => setOnlyMine(false)}
            class={`rounded px-2.5 py-1 text-xs font-medium transition-colors min-h-[26px] ${
              !onlyMine
                ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
            }`}
            aria-pressed={!onlyMine}
          >
            Alle
          </button>
          <button
            type="button"
            onClick={() => setOnlyMine(true)}
            class={`rounded px-2.5 py-1 text-xs font-medium transition-colors min-h-[26px] ${
              onlyMine
                ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
            }`}
            aria-pressed={onlyMine}
            disabled={!me.user}
          >
            Nur meine
          </button>
        </div>
      </div>

      <div class="flex gap-3">
        {/* Calendar grid */}
        <div class="flex-1 min-w-0 rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] overflow-hidden">
          {/* Header row */}
          <div
            class="grid grid-cols-7 bg-[color:var(--color-surface-container-low)] border-b border-[color:var(--color-outline-variant)]"
            role="row"
          >
            {WEEKDAYS.map((d) => (
              <span
                key={d}
                class="px-2 py-1.5 text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)] font-semibold text-center"
              >
                {d}
              </span>
            ))}
          </div>

          {/* Weeks */}
          <DragDropContext onDragEnd={(r) => void onDragEnd(r)}>
            <div class="flex flex-col">
              {weeks.map((week, wi) => (
                <CalendarWeek
                  key={wi}
                  week={week}
                  weekIndex={wi}
                  bars={multiDayPerWeek[wi] ?? []}
                  singleDayMap={singleDayMap}
                  phaseById={phaseById}
                  projectColor={projectColor}
                  today={today}
                  selectedDay={selectedDay}
                  onSelectDay={setSelectedDay}
                  onOpenTask={setOpenTaskId}
                  inMonth={cursor.getMonth()}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </DragDropContext>

          {/* Legend */}
          <div class="flex flex-wrap items-center gap-3 px-3 py-2 text-label text-[color:var(--color-on-surface-variant)] border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)]">
            <span class="font-semibold uppercase tracking-wider">Legende</span>
            {project.phases.length > 0 ? (
              project.phases.map((p) => (
                <span key={p.id} class="inline-flex items-center gap-1">
                  <span
                    class="inline-block size-2 rounded-sm"
                    style={{
                      background: p.color ?? projectColor,
                    }}
                    aria-hidden="true"
                  />
                  {p.name}
                </span>
              ))
            ) : (
              <span class="italic">Keine Phasen im Projekt</span>
            )}
          </div>
        </div>

        {/* Side panel for the selected day */}
        {selectedDay && (
          <aside
            class="hidden md:flex w-[320px] shrink-0 flex-col rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-2 max-h-[640px]"
            aria-label="Aufgaben dieses Tages"
          >
            <header class="flex items-center justify-between gap-2 px-3 py-2 border-b border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] rounded-t-lg">
              <h3 class="text-sm font-semibold text-[color:var(--color-on-surface)]">
                {formatDayHeader(selectedDay)}
              </h3>
              <button
                type="button"
                onClick={() => setSelectedDay(null)}
                class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                aria-label="Schließen"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </header>
            <div class="flex-1 overflow-auto p-3 flex flex-col gap-2">
              {tasksForSelectedDay.length === 0 ? (
                <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                  Keine Aufgaben an diesem Tag
                </p>
              ) : (
                tasksForSelectedDay.map((t) => (
                  <SidePanelTask
                    key={t.id}
                    task={t}
                    phase={phaseById[t.phase_id] ?? null}
                    projectColor={projectColor}
                    onOpen={() => setOpenTaskId(t.id)}
                  />
                ))
              )}
            </div>
          </aside>
        )}
      </div>

      {/* Mobile: side panel as bottom sheet */}
      {selectedDay && (
        <div
          class="md:hidden fixed inset-x-0 bottom-0 z-40 max-h-[60vh] flex flex-col rounded-t-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-3"
          role="dialog"
          aria-label="Aufgaben dieses Tages"
        >
          <header class="flex items-center justify-between gap-2 px-3 py-2 border-b border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] rounded-t-lg">
            <h3 class="text-sm font-semibold text-[color:var(--color-on-surface)]">
              {formatDayHeader(selectedDay)}
            </h3>
            <button
              type="button"
              onClick={() => setSelectedDay(null)}
              class="rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
              aria-label="Schließen"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </header>
          <div class="flex-1 overflow-auto p-3 flex flex-col gap-2">
            {tasksForSelectedDay.length === 0 ? (
              <p class="text-xs italic text-[color:var(--color-on-surface-variant)]">
                Keine Aufgaben an diesem Tag
              </p>
            ) : (
              tasksForSelectedDay.map((t) => (
                <SidePanelTask
                  key={t.id}
                  task={t}
                  phase={phaseById[t.phase_id] ?? null}
                  projectColor={projectColor}
                  onOpen={() => setOpenTaskId(t.id)}
                />
              ))
            )}
          </div>
        </div>
      )}

      {openTask && (
        <TaskModal
          task={openTask}
          siblings={allTasks}
          projectId={project.id}
          phases={project.phases}
          onClose={() => setOpenTaskId(null)}
          onDeleted={() => setOpenTaskId(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

// ---------- week + day rendering ----------

interface WeekDay {
  iso: string;
  date: Date;
  inMonth: boolean;
}

interface MultiDayBar {
  task: Task;
  startCol: number; // 0..6
  span: number; // 1..7
  // See CalendarBarSegment in lib/calendarBars — false means this edge is a
  // week-boundary clip, not the task's real start/end, so the bar's corner
  // is flattened there instead of rounded (#42 continuation cue).
  isStart: boolean;
  isEnd: boolean;
}

function CalendarWeek(props: {
  week: WeekDay[];
  weekIndex: number;
  bars: MultiDayBar[];
  singleDayMap: Record<string, Task[]>;
  phaseById: Record<number, Phase>;
  projectColor: string;
  today: string;
  selectedDay: string | null;
  onSelectDay: (iso: string) => void;
  onOpenTask: (id: number) => void;
  inMonth: number;
  readOnly: boolean;
}): preact.JSX.Element {
  const {
    week,
    weekIndex,
    bars,
    singleDayMap,
    phaseById,
    projectColor,
    today,
    selectedDay,
    onSelectDay,
    onOpenTask,
    readOnly,
  } = props;

  // Stack bars: simple row assignment so two bars don't overlap.
  const stackedBars = stackBars(bars);
  const barRowCount = stackedBars.reduce((m, b) => Math.max(m, b.row + 1), 0);
  const barAreaHeight = barRowCount * 22 + (barRowCount > 0 ? 4 : 0);
  const weekStartIso = week[0].iso;

  return (
    <div class="border-t border-[color:var(--color-outline-variant)] first:border-t-0">
      {/* Bars row: positioned over the 7 columns above the day chips. This
          Droppable is a drag SOURCE only (isDropDisabled) — it has no
          per-day resolution, so a bar dropped back onto it (rather than onto
          a specific day cell below) is treated as a no-op by onDragEnd. */}
      {barAreaHeight > 0 && (
        <Droppable
          droppableId={`bars-week-${weekIndex}`}
          type={CAL_DND_TYPE}
          isDropDisabled
        >
          {(barProv) => (
            <div
              ref={barProv.innerRef}
              {...barProv.droppableProps}
              class="relative grid grid-cols-7 px-1 pt-1"
              style={{ height: `${barAreaHeight}px` }}
              aria-hidden="false"
            >
              {stackedBars.map(({ bar, row }, idx) => {
                const phase = phaseById[bar.task.phase_id] ?? null;
                const color = phase?.color ?? projectColor;
                // Drag anchor: the segment's own first visible day in this
                // week (not necessarily the task's overall start_date, which
                // may lie in an earlier week) — see the DnD-semantics
                // comment block at the top of the file.
                const anchorIso = addDaysIso(weekStartIso, bar.startCol);
                return (
                  <Draggable
                    key={`${bar.task.id}-${bar.startCol}`}
                    draggableId={`bar:${bar.task.id}:${anchorIso}`}
                    index={idx}
                    isDragDisabled={readOnly}
                  >
                    {(dragProv, dragSnap) => (
                      <button
                        ref={dragProv.innerRef}
                        {...(dragProv.draggableProps as preact.JSX.HTMLAttributes<HTMLButtonElement>)}
                        {...(dragProv.dragHandleProps as preact.JSX.HTMLAttributes<HTMLButtonElement> | undefined)}
                        type="button"
                        onClick={() => onOpenTask(bar.task.id)}
                        style={{
                          ...((dragProv.draggableProps.style ?? {}) as preact.JSX.CSSProperties),
                          gridColumn: `${bar.startCol + 1} / span ${bar.span}`,
                          marginTop: `${row * 22}px`,
                          background: color,
                          color: barFg(color),
                        }}
                        class={`relative h-[18px] truncate px-1.5 text-label font-medium text-left hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-opacity ${
                          // Flatten the edge where a bar is clipped by a week
                          // boundary rather than the task's real start/end,
                          // so a continuation into the next/previous week's
                          // segment reads as one bar, not two (#42).
                          bar.isStart ? "rounded-l-sm" : "rounded-l-none"
                        } ${bar.isEnd ? "rounded-r-sm" : "rounded-r-none"} ${
                          bar.task.status === "done" ? "opacity-60 line-through" : ""
                        } ${dragSnap.isDragging ? "shadow-level-2" : ""}`}
                        title={`${bar.task.title} (${bar.task.start_date} – ${bar.task.end_date})`}
                      >
                        {bar.task.title}
                      </button>
                    )}
                  </Draggable>
                );
              })}
              {barProv.placeholder}
            </div>
          )}
        </Droppable>
      )}

      <div class="grid grid-cols-7" role="row">
        {week.map((d) => {
          const chips = singleDayMap[d.iso] ?? [];
          const isToday = d.iso === today;
          const isSelected = selectedDay === d.iso;
          const isWeekend = d.date.getDay() === 0 || d.date.getDay() === 6;

          return (
            <Droppable key={d.iso} droppableId={`day:${d.iso}`} type={CAL_DND_TYPE} isDropDisabled={readOnly}>
              {(dayProv, daySnap) => (
                <button
                  ref={dayProv.innerRef}
                  {...dayProv.droppableProps}
                  type="button"
                  onClick={() => onSelectDay(d.iso)}
                  class={`group min-h-[88px] md:min-h-[100px] border-l border-[color:var(--color-outline-variant)] first:border-l-0 p-1.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 focus:relative focus:z-10 ${
                    daySnap.isDraggingOver
                      ? "ring-2 ring-inset ring-[color:var(--color-primary)] bg-[color:var(--color-surface-container-high)]"
                      : isSelected
                        ? "bg-[color:var(--color-surface-container-high)]"
                        : isToday
                          ? "bg-[color:var(--color-surface-container-low)]"
                          : isWeekend
                            ? "bg-[color:var(--color-surface-container-low)]/40"
                            : "bg-[color:var(--color-surface-container-lowest)]"
                  } hover:bg-[color:var(--color-surface-container-low)]`}
                  aria-pressed={isSelected}
                  aria-label={`${d.date.toLocaleDateString("de-DE", { day: "numeric", month: "long" })}${chips.length > 0 ? `, ${chips.length} ${chips.length === 1 ? "Aufgabe" : "Aufgaben"}` : ""}`}
                >
                  <div class="flex items-center justify-between gap-1">
                    <span
                      class={`inline-flex size-5 items-center justify-center rounded-full text-micro font-semibold ${
                        isToday
                          ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)]"
                          : d.inMonth
                            ? "text-[color:var(--color-on-surface)]"
                            : "text-[color:var(--color-on-surface-variant)] opacity-60"
                      }`}
                    >
                      {d.date.getDate()}
                    </span>
                    {chips.length > 3 && (
                      <span class="text-label font-medium text-[color:var(--color-on-surface-variant)]">
                        +{chips.length - 3}
                      </span>
                    )}
                  </div>

                  <div class="mt-1 flex flex-col gap-0.5">
                    {chips.slice(0, 3).map((t, chipIdx) => {
                      const phase = phaseById[t.phase_id] ?? null;
                      const color = phase?.color ?? projectColor;
                      return (
                        <Draggable
                          key={t.id}
                          draggableId={`chip:${t.id}`}
                          index={chipIdx}
                          isDragDisabled={readOnly}
                        >
                          {(dragProv, dragSnap) => (
                            <span
                              ref={dragProv.innerRef}
                              {...(dragProv.draggableProps as preact.JSX.HTMLAttributes<HTMLSpanElement>)}
                              {...(dragProv.dragHandleProps as preact.JSX.HTMLAttributes<HTMLSpanElement> | undefined)}
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenTask(t.id);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onOpenTask(t.id);
                                }
                              }}
                              class={`inline-flex items-center gap-1 truncate rounded-sm px-1 py-0.5 text-label cursor-pointer hover:opacity-80 transition-opacity ${
                                t.status === "done" ? "opacity-60 line-through" : ""
                              } ${dragSnap.isDragging ? "shadow-level-2" : ""}`}
                              style={{
                                ...((dragProv.draggableProps.style ?? {}) as preact.JSX.CSSProperties),
                                // color-mix statt Hex-Alpha-Konkatenation: der Fallback
                                // ist "var(--color-primary)", und "var(…)22" wäre kein
                                // gültiger Farbwert (Chip bliebe ungefüllt).
                                background: `color-mix(in srgb, ${color} 14%, transparent)`,
                                color: "var(--color-on-surface)",
                                borderLeft: `2px solid ${color}`,
                              }}
                              title={t.ticket ? `${t.ticket} · ${t.title}` : t.title}
                            >
                              {t.is_milestone && (
                                <Diamond size={14} aria-hidden="true" class="shrink-0" fill="currentColor" />
                              )}
                              {t.ticket && (
                                <span class="select-text font-mono text-label font-medium opacity-70 shrink-0">
                                  {t.ticket}
                                </span>
                              )}
                              <span class="truncate">{t.title}</span>
                            </span>
                          )}
                        </Draggable>
                      );
                    })}
                    {dayProv.placeholder}
                  </div>
                </button>
              )}
            </Droppable>
          );
        })}
      </div>
    </div>
  );
}

function SidePanelTask(props: {
  task: Task;
  phase: Phase | null;
  projectColor: string;
  onOpen: () => void;
}): preact.JSX.Element {
  const { task, phase, projectColor, onOpen } = props;
  const assignee = findUser(task.primary_assignee_id);
  const color = phase?.color ?? projectColor;
  const rel = relativeDay(task.deadline ?? task.end_date);

  return (
    <button
      type="button"
      onClick={onOpen}
      class="text-left rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2.5 py-2 hover:border-[color:var(--color-primary)] hover:shadow-level-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-all"
    >
      <div class="flex items-start gap-2">
        <span
          class="mt-1 inline-block size-2 shrink-0 rounded-full"
          style={{ background: color }}
          aria-hidden="true"
        />
        <div class="flex-1 min-w-0">
          <p
            class={`text-sm font-medium text-[color:var(--color-on-surface)] leading-snug ${
              task.status === "done" ? "line-through opacity-60" : ""
            }`}
          >
            {task.title}
          </p>
          <div class="mt-1 flex items-center flex-wrap gap-1.5 text-label text-[color:var(--color-on-surface-variant)]">
            <span
              class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold"
              style={softPillStyle(TASK_STATUS_VAR[task.status])}
            >
              {TASK_STATUS_LABEL[task.status]}
            </span>
            {task.priority && (
              <span
                class="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-semibold"
                style={softPillStyle(TASK_PRIORITY_VAR[task.priority])}
              >
                {TASK_PRIORITY_LABEL[task.priority]}
              </span>
            )}
            {phase && <span>{phase.name}</span>}
            {assignee && (
              <span class="inline-flex items-center gap-1">
                · {assignee.name}
                {assignee.is_bot && (
                  <BotBadge ownerId={assignee.bot_owner_id} />
                )}
              </span>
            )}
            {rel && (
              <span
                class={rel.overdue ? "text-[color:var(--color-error)] font-medium" : ""}
              >
                · {rel.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ---------- date helpers ----------

function isoLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseIsoDate(iso: string | null): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The subset of Task's date fields a single-day chip's placement depends on. */
export interface ChipDateFields {
  deadline: string | null;
  start_date: string | null;
  end_date: string | null;
}

export interface ChipDatePatch {
  deadline?: string;
  start_date?: string;
  end_date?: string;
}

/**
 * Single-day chip drop: rewrite whichever field placed the chip on its
 * current day (same `deadline ?? end_date ?? start_date` priority
 * classifyTasks() uses), so the write-back always matches what the user saw
 * move. Returns null (no-op) when there's nothing to move, or the drop
 * landed back on the same day.
 */
export function computeChipDatePatch(
  task: ChipDateFields,
  targetIso: string
): ChipDatePatch | null {
  const current = task.deadline ?? task.end_date ?? task.start_date;
  if (!current || current === targetIso) return null;
  if (task.deadline) return { deadline: targetIso };
  if (task.start_date && task.end_date && task.start_date === task.end_date) {
    // Zero-duration span (start === end, no deadline) — keep both in sync.
    return { start_date: targetIso, end_date: targetIso };
  }
  if (task.end_date) return { end_date: targetIso };
  return { start_date: targetIso };
}

/**
 * Multi-day bar drop: shift BOTH start_date and end_date by the day-delta
 * between the segment's drag anchor and the target day, preserving the
 * span's duration. Returns null (no-op) when dropped back on the anchor day.
 */
export function computeSpanShiftPatch(
  startIso: string,
  endIso: string,
  anchorIso: string,
  targetIso: string
): { start_date: string; end_date: string } | null {
  if (anchorIso === targetIso) return null;
  const delta = diffDaysIso(anchorIso, targetIso);
  return { start_date: addDaysIso(startIso, delta), end_date: addDaysIso(endIso, delta) };
}

function formatDayHeader(iso: string): string {
  const d = parseIsoDate(iso);
  if (!d) return iso;
  return d.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Build a Mon-Sun aligned grid of 5 or 6 weeks containing the given month.
 * The first cell is the Monday of (or before) the 1st of the month.
 */
function buildGrid(monthStart: Date): WeekDay[] {
  const first = new Date(monthStart);
  const dow = (first.getDay() + 6) % 7; // 0=Mon ... 6=Sun
  const start = new Date(first);
  start.setDate(first.getDate() - dow);

  const lastOfMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0
  );
  const lastDow = (lastOfMonth.getDay() + 6) % 7;
  const end = new Date(lastOfMonth);
  end.setDate(lastOfMonth.getDate() + (6 - lastDow));

  const days: WeekDay[] = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    days.push({
      iso: isoLocal(cursor),
      date: new Date(cursor),
      inMonth: cursor.getMonth() === monthStart.getMonth(),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function chunkWeeks(grid: WeekDay[]): WeekDay[][] {
  const out: WeekDay[][] = [];
  for (let i = 0; i < grid.length; i += 7) {
    out.push(grid.slice(i, i + 7));
  }
  return out;
}

/**
 * - singleDayMap: ISO date → tasks (deadline OR same-day start=end).
 * - multiDayPerWeek: per visible week, bars that span >=2 days within that week.
 */
function classifyTasks(
  tasks: Task[],
  weeks: WeekDay[][]
): {
  singleDayMap: Record<string, Task[]>;
  multiDayPerWeek: Record<number, MultiDayBar[]>;
} {
  const singleDayMap: Record<string, Task[]> = {};
  const multiDayPerWeek: Record<number, MultiDayBar[]> = {};
  const weekStartIsos = weeks.map((w) => w[0].iso);

  for (const t of tasks) {
    // Determine kind:
    // - Both dates and end > start → multi-day bar
    // - deadline only → chip on the deadline day
    // - start_date only → chip on start
    // - same-day → chip
    if (t.start_date && t.end_date && t.end_date > t.start_date) {
      // Multi-day bar — one segment per week it touches (#42), clipped to
      // the grid's edges. Pure splitting logic lives in lib/calendarBars so
      // it's unit-testable without mounting this view.
      for (const seg of splitTaskIntoWeekSegments(t.start_date, t.end_date, weekStartIsos)) {
        const list = multiDayPerWeek[seg.weekIndex] ?? (multiDayPerWeek[seg.weekIndex] = []);
        list.push({
          task: t,
          startCol: seg.startCol,
          span: seg.span,
          isStart: seg.isStart,
          isEnd: seg.isEnd,
        });
      }
    } else {
      const iso = t.deadline ?? t.end_date ?? t.start_date;
      if (!iso) continue;
      const list = singleDayMap[iso] ?? (singleDayMap[iso] = []);
      list.push(t);
    }
  }

  // Sort the day chips so high-priority comes first within a cell.
  for (const list of Object.values(singleDayMap)) {
    list.sort(
      (a, b) =>
        PRIO_RANK[a.priority ?? "none"] - PRIO_RANK[b.priority ?? "none"] ||
        a.title.localeCompare(b.title, "de")
    );
  }
  return { singleDayMap, multiDayPerWeek };
}

/**
 * Assign each bar a row so two bars within the same week never overlap.
 * Greedy: sort by start, place each on the lowest free row.
 */
function stackBars(
  bars: MultiDayBar[]
): { bar: MultiDayBar; row: number }[] {
  const sorted = [...bars].sort((a, b) => a.startCol - b.startCol || b.span - a.span);
  const rows: { end: number }[] = []; // rows[i].end = last occupied col + 1
  const out: { bar: MultiDayBar; row: number }[] = [];
  for (const bar of sorted) {
    let row = 0;
    for (; row < rows.length; row++) {
      if (rows[row].end <= bar.startCol) break;
    }
    rows[row] = { end: bar.startCol + bar.span };
    out.push({ bar, row });
  }
  return out;
}

/**
 * Tasks that overlap a specific day:
 * - single-day if deadline == day OR start == end == day
 * - multi-day if start <= day <= end
 */
function collectDayTasks(tasks: Task[], iso: string): Task[] {
  const day = parseIsoDate(iso);
  if (!day) return [];
  const out: Task[] = [];
  for (const t of tasks) {
    const start = parseIsoDate(t.start_date);
    const end = parseIsoDate(t.end_date);
    const deadline = parseIsoDate(t.deadline);
    let hit = false;
    if (start && end && end.getTime() > start.getTime()) {
      hit = start.getTime() <= day.getTime() && day.getTime() <= end.getTime();
    } else if (start && (!end || end.getTime() === start.getTime())) {
      hit = start.getTime() === day.getTime();
    } else if (end) {
      hit = end.getTime() === day.getTime();
    }
    if (!hit && deadline) {
      if (deadline.getTime() === day.getTime()) hit = true;
    }
    if (hit) out.push(t);
  }
  return out;
}
